import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Dropdown } from 'react-bootstrap';
import classnames from 'classnames';
import { FaUndo, FaFilter } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import CrmTypeButtonGroup from '@/shared/components/CrmTypeButtonGroup';
import RelationTypeIcon from '@/shared/components/RelationTypeIcon';
import CheckboxDropdownMenu from '@/shared/components/input/CheckboxDropdownMenu';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import MultiSelect, {
  removeItem as removeMultiSelectItem,
  addItem as addMultiSelectItem,
  splitItems as splitMultiSelectItems,
} from '@/shared/components/input/multiselect/MultiSelect';
import api from '@/shared/api';
import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import enumCache from '@/shared/services/EnumCacheService';
import RelationSearch from '@/shared/components/input/RelationSearch';
import userStorage from '@/shared/services/userStorage';

import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  initDynamicFilters,
} from '@/shared/components/DynamicFilter';
import messages from '../relations/overview/relationOverviewMessages';
import config from '../../config';

export const defaultRelationFilters = {
  amlEndScore: null,
  assistant: null, // UserId
  assistantBackup: null, // UserId
  associate: null, // UserId
  responsible: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  relationId: null, // RelationId
  relationName: null, // string
  type: [], // Array
  crmTypes: [], // Array
  legalForms: [], // Array
  withLabels: [], // Array
  withoutLabels: [], // Array
  linkType: null,
  ownCompanies: [],
  legalEntities: [],
  statusFilters: [],
  assignedTo: null, // UserId
  naceCodes: [],
  filterBehaviours: {
    withLabels: 1,
    naceCodes: 1,
  },
  relationGroups: [],
  pep: null,
  linkedToPep: null,
  nationalityCode: '',
  relationHandlingLevels: [],
  shareRegisters: [],
  onlyNotFullyCompliant: false,
};

const dynamicFiltersStorageKey = 'ComplianceOverviewDynamicFilters';

class CompliancyFilters extends Component {
  state = {
    labels: [],
    linkTypes: [],
    dynamicFilters: [],
    countries: [],
  };

  static propTypes = {
    countSelectedRelations: PropTypes.string.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      associate: PropTypes.string,
      responsible: PropTypes.string,
      assistant: PropTypes.string,
      assistantBackup: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      searchValue: PropTypes.string,
      assignedTo: PropTypes.string,
      type: PropTypes.arrayOf(PropTypes.number),
      crmTypes: PropTypes.arrayOf(PropTypes.number),
      legalForms: PropTypes.arrayOf(PropTypes.number),
      withLabels: PropTypes.arrayOf(PropTypes.string),
      withoutLabels: PropTypes.arrayOf(PropTypes.string),
      linkType: PropTypes.number,
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      naceCodes: PropTypes.arrayOf(PropTypes.string),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      relationHandlingLevels: PropTypes.arrayOf(PropTypes.number),
    }),
    intl: intlShape.isRequired,
    // Show reset filter button
    hasFilters: PropTypes.bool,
    onResetFilters: PropTypes.func,
  };

  static defaultProps = {
    filters: defaultRelationFilters,
    hasFilters: false,
    onResetFilters: () => {},
  };

  componentDidMount() {
    const { intl, filters } = this.props;

    Promise.all([
      api.base.get(`${config.appUrls.api}/api/Resource/Tags`),
      enumCache.get('linktype'),
      enumCache.get('shareregister'),
    ]).then(([{ data: tags }, linkTypes, shareRegister]) => {
      const dataCache = DataCacheService.getDataCache();

      const allDynamicFilters = [
        ...getDynamicFiltersFromDefaultFilters(defaultRelationFilters, 'crmTypes'),

        {
          id: 'naceCode',
          label: messages.NaceCode,
          enabled: DataCacheService.isBelgianTenantConfiguration(),
        },
        {
          id: 'linkType',
          label: messages.FilterLinkType,
        },
        { id: 'nationalityCode', label: messages.Nationality },
        { id: 'shareRegisters', label: messages.ShareRegister },
      ];

      const specificCountryCodes = ['BE', 'NL', 'FR', 'LU', 'GB'];

      // Create an array for the specific country codes in the format you want
      const specificCountryOptions = specificCountryCodes
        .map(code => {
          const country = dataCache.country.find(x => x.code === code);
          return {
            id: country ? country.code : code,
            label: intl.formatMessage(listItemMsg('country', code, country?.description || '')),
          };
        })
        .sort(dynamicSort('label'));

      // Create an array for "All other countries" option
      const otherCountryOptions = dataCache.country
        .filter(x => !specificCountryCodes.includes(x.code))
        .map(x => ({
          id: x.code,
          label: intl.formatMessage(listItemMsg('country', x.code, x.description || '')),
        }))
        .sort(dynamicSort('label')); // Sort the array based on the translated labels

      // Combine the arrays of specific country codes and all other countries
      const combinedOptions = [...specificCountryOptions, ...otherCountryOptions];

      let shareRegisters = [
        { id: -1, label: intl.formatMessage(messages.ShareRegisterNotFilledOut) },
        ...shareRegister.map(x => ({
          id: x.key,
          label: intl.formatMessage(listItemMsg('shareRegister', x.key, x.value)),
        })),
      ];

      this.setState({
        naceCodes: dataCache.naceCodes.map(x => ({
          id: x.Code,
          label: `${x.Code} - ${x.Name}`,
        })),
        countries: combinedOptions,

        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(dynamicFiltersStorageKey) || [],
          filters,
          defaultRelationFilters,
        ),
        labels: tags
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase(), color: x.color }))
          .sort(dynamicSort('sort')),
        linkTypes: linkTypes
          .map(x => ({
            id: x.key,
            label: intl.formatMessage(listItemMsg('linktypeto', x.key, x.value)),
          }))
          .sort(dynamicSort('label')),
        shareRegisters,
      });
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (filters !== newFilters) onFilterChange(newFilters);
  };

  filterRelationTypes = relType => {
    const { filters = {} } = this.props;
    const types = filters.type || [];

    const idx = types.indexOf(relType);
    if (idx >= 0) {
      this.filterChanged({ type: types.filter((_, i) => i !== idx) });
    } else {
      this.filterChanged({ type: [...types, relType] });
    }
  };

  getRelationTypes = () => {
    const { intl } = this.props;
    return [
      {
        id: 0,
        label: intl.formatMessage(listItemMsg('relationtype', 0, 'Company')),
      },
      {
        id: 1,
        label: intl.formatMessage(listItemMsg('relationtype', 1, 'SoleProprietor')),
      },
      {
        id: 2,
        label: intl.formatMessage(listItemMsg('relationtype', 2, 'Person')),
      },
    ];
  };

  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'linkType': {
        const { linkTypes } = this.state;

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({ linkType: e.target.value ? parseInt(e.target.value) : null })
                }
                value={filters.linkType || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterLinkType)}</option>
                {linkTypes.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'naceCode': {
        const { naceCodes } = this.state;

        const [selectedNaceCodes, availableNaceCodes] = splitMultiSelectItems(
          naceCodes,
          filters.naceCodes,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedNaceCodes}
                onItemRemoved={item =>
                  this.filterChanged({
                    naceCodes: removeMultiSelectItem(filters.naceCodes, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    naceCodes: addMultiSelectItem(filters.naceCodes, item),
                  })
                }
                items={availableNaceCodes}
                placeholder={intl.formatMessage(messages.NaceCode)}
                onFilterBehaviourChanged={filterBehaviour => {
                  this.filterChanged({
                    filterBehaviours: {
                      naceCodes: filterBehaviour,
                    },
                  });
                }}
                filterBehaviour={filters.filterBehaviours?.naceCodes}
                withFilterBehaviour
              />
            </div>
          </div>
        );
      }

      case 'nationalityCode': {
        const { countries } = this.state;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({ nationalityCode: e.target.value ? e.target.value : null })
                }
                value={filters.nationalityCode || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterNationality)}</option>
                {countries.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }

      case 'shareRegisters': {
        const { shareRegisters } = this.state;
        const [selectedShareRegisters, availableShareRegisters] = splitMultiSelectItems(
          shareRegisters,
          filters.shareRegisters,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedShareRegisters}
                onItemRemoved={item =>
                  this.filterChanged({
                    shareRegisters: removeMultiSelectItem(filters.shareRegisters, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    shareRegisters: addMultiSelectItem(filters.shareRegisters, item),
                  })
                }
                items={availableShareRegisters}
                placeholder={intl.formatMessage(messages.ShareRegister)}
              />
            </div>
          </div>
        );
      }

      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };
  dynamicFilterChanged = (item, selected, visibleItemIds) => {
    const { dynamicFilters } = this.state;
    const { filters } = this.props;

    const newFilters = dynamicFilters.map(x => {
      if (x.id !== item.id && item.id !== 'selectall') return x;
      if (visibleItemIds && !visibleItemIds.includes(x.id)) return x;
      return { ...x, selected };
    });

    userStorage.setItem(
      dynamicFiltersStorageKey,
      newFilters.filter(x => x.selected).map(x => x.id),
    );
    this.setState({
      dynamicFilters: newFilters,
    });

    if (
      !selected &&
      JSON.stringify(filters[item.id]) !== JSON.stringify(defaultRelationFilters[item.id])
    ) {
      // Reset filter when it's removed
      this.filterChanged({ [item.id]: defaultRelationFilters[item.id] });
    }
  };

  render() {
    const { filters, intl, hasFilters, onResetFilters, countSelectedRelations } = this.props;
    const { labels, dynamicFilters } = this.state;
    const defaultBtnClass = 'btn btn-default';
    const relationTypes = this.getRelationTypes();

    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;
    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    const dropdownFilters = [
      {
        id: 'selectall',
        label: intl.formatMessage(messages.SelectAll),
        selected: !dynamicFilters.some(x => !x.selected),
      },
      ...dynamicFilters,
    ];

    return (
      <div>
        <div className="row">
          <div className="col-lg-4">
            <div className="form-group">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
          </div>
          <div className="col-lg-4 col-sm-6">
            <div role="group" aria-label="relationType" className="btn-group btn-group-justified">
              {relationTypes.map(t => (
                <button
                  key={`RelTypeFilter${t.id}`}
                  onClick={() => {
                    document.activeElement.blur();
                    this.filterRelationTypes(t.id);
                  }}
                  type="button"
                  title={t.label}
                  className={classnames(defaultBtnClass, {
                    active: filters && filters.type && filters.type.includes(t.id),
                  })}
                >
                  <RelationTypeIcon type={t.id} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <CrmTypeButtonGroup
                selectedTypes={filters.crmTypes}
                onChange={value => {
                  this.filterChanged({ crmTypes: value });
                }}
                hiddenTypes={[4]}
              />
            </div>
          </div>
        </div>
        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row hidden-xs hidden-sm">
            {visibleFilters.map(id => (
              <DynamicFilter
                key={id}
                type={id}
                intl={intl}
                labels={labels}
                filters={filters}
                filterChanged={this.filterChanged}
                renderCustomFilter={this.renderDynamicFilter}
              />
            ))}
          </div>
        ) : null}
        <div className="row hidden-xs hidden-sm">
          <div className="col-sm-12">
            <Dropdown id="dynamicFilterMenu">
              <Dropdown.Toggle title={intl.formatMessage(messages.SelectYourFilters)}>
                <FaFilter className="react-icon" />
                <FormattedMessage {...messages.MoreFilters} />
              </Dropdown.Toggle>
              <CheckboxDropdownMenu
                bsRole="menu"
                items={dropdownFilters}
                onItemChanged={this.dynamicFilterChanged}
              />
            </Dropdown>
            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset margin10L"
                onClick={() => onResetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}

            <div className="pull-right margin10T">{countSelectedRelations}</div>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(CompliancyFilters);
